import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import FieldSeasonTable, { FieldSeasonItem } from './FieldSeasonTable';
import FieldSeasonIrrigationDialog from './FieldSeasonIrrigationDialog';

interface Props {
  businessId: string;
  authHeader: { Authorization: string };
  isAdmin: boolean;
}

// The business page's "Other Records" tab: its field seasons (yield,
// irrigation, water use, previous crop, ...), which an import fills
// without creating any Record — see api/Models/FieldSeason.cs. Child
// businesses are included, same as the Records tab.
const FieldSeasonsPanel: React.FC<Props> = ({ businessId, authHeader, isAdmin }) => {
  const [farms, setFarms] = useState<{ id: string; name: string; businessName: string }[]>([]);
  const [timePeriods, setTimePeriods] = useState<{ id: string; name: string }[]>([]);
  const [farmId, setFarmId] = useState('');
  const [timePeriodId, setTimePeriodId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [items, setItems] = useState<FieldSeasonItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [irrigationItem, setIrrigationItem] = useState<FieldSeasonItem | null>(null);

  useEffect(() => {
    fetch('/api/time-periods', { headers: authHeader })
      .then((res) => (res.ok ? res.json() : []))
      .then(setTimePeriods)
      .catch(() => setTimePeriods([]));
    fetch(`/api/businesses/${businessId}/records/farms`, { headers: authHeader })
      .then((res) => (res.ok ? res.json() : []))
      .then(setFarms)
      .catch(() => setFarms([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(page + 1), pageSize: String(pageSize) });
      if (farmId) params.set('farmId', farmId);
      if (timePeriodId) params.set('timePeriodId', timePeriodId);
      if (search) params.set('search', search);
      const res = await fetch(`/api/businesses/${businessId}/field-seasons?${params}`, { headers: authHeader });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setItems(data.items);
      setTotalCount(data.totalCount);
    } catch {
      setError('Failed to load field seasons');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, farmId, timePeriodId, search, page, pageSize]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Debounce free-text search so it doesn't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const resetFilters = () => {
    setFarmId('');
    setTimePeriodId('');
    setSearchInput('');
    setSearch('');
    setPage(0);
  };

  const handleArchive = async (item: FieldSeasonItem) => {
    if (!window.confirm(`Archive the ${item.timePeriodName} season of ${item.fieldName}? Supplementary import rows that filled it go back to unmatched.`)) return;
    try {
      const res = await fetch(`/api/field-seasons/${item.id}`, { method: 'DELETE', headers: authHeader });
      if (!res.ok) throw new Error();
      await fetchItems();
    } catch {
      setError('Failed to archive field season');
    }
  };

  // Farms from child businesses are listed too, so the business name tells
  // same-named farms apart — and the table gets a Business column.
  const multipleBusinesses = new Set(farms.map((f) => f.businessName)).size > 1;

  return (
    <Box sx={{ p: 2, flexGrow: 1, minHeight: 0, overflow: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6">Field Seasons</Typography>
        <IconButton size="small" onClick={fetchItems} title="Refresh"><RefreshIcon fontSize="small" /></IconButton>
        <Box sx={{ flexGrow: 1 }} />
        <TextField
          size="small"
          placeholder="Search farm, field, variety…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="field-seasons-farm-label">Farm</InputLabel>
          <Select labelId="field-seasons-farm-label" label="Farm" value={farmId} onChange={(e) => { setFarmId(e.target.value); setPage(0); }}>
            <MenuItem value=""><em>Any</em></MenuItem>
            {farms.map((f) => (
              <MenuItem key={f.id} value={f.id}>{multipleBusinesses ? `${f.name} (${f.businessName})` : f.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="field-seasons-time-period-label">Time Period</InputLabel>
          <Select labelId="field-seasons-time-period-label" label="Time Period" value={timePeriodId} onChange={(e) => { setTimePeriodId(e.target.value); setPage(0); }}>
            <MenuItem value=""><em>Any</em></MenuItem>
            {timePeriods.map((tp) => (
              <MenuItem key={tp.id} value={tp.id}>{tp.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <IconButton size="small" onClick={resetFilters} title="Clear filters">
          <RestartAltIcon fontSize="small" />
        </IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading && items.length === 0 ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress size={24} /></Box>
      ) : items.length === 0 ? (
        <Typography color="text.secondary">No field seasons found.</Typography>
      ) : (
        <FieldSeasonTable
          items={items}
          totalCount={totalCount}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
          showBusiness={multipleBusinesses}
          onArchive={isAdmin ? handleArchive : undefined}
          onEditIrrigation={isAdmin ? setIrrigationItem : undefined}
        />
      )}

      <FieldSeasonIrrigationDialog
        item={irrigationItem}
        authHeader={authHeader}
        onClose={() => setIrrigationItem(null)}
        onSaved={() => { setIrrigationItem(null); fetchItems(); }}
      />
    </Box>
  );
};

export default FieldSeasonsPanel;
